import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import '../styles/ScrollableImages.css';
import { Arrow } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { propertyConditionBG } from '../functions';
import { Condition, FilterChip, FinishChip, SourceBadge } from './';
import { buildSourceLookup, resolveImageSource } from '../utils/imageSourceUtils';
import CoLivingPlanTracker from './CoLivingPlanTracker';
import { resolveRoomBucket, resolveSubRoomBadge } from '../utils/imageLabelUtils';

// Must stay in sync with the classifier's roomType enum at
// functions/underwritingV3/imageClassifier.js — any classifier value missing
// here gets silently bucketed into "Other". "porch" and "floor_plan" are NOT
// classifier room types; they come from the label's `displayCategory` (derived
// from the is_floor_plan / outdoor_or_screened_visible atoms — see
// functions/utils/imageDisplayCategory.js) and the bucketer prefers it over roomType.
// The floor plan's own room type (`planRoomType`) also outranks roomType — see
// resolveRoomBucket in src/utils/imageLabelUtils.js for the full precedence.
// "ai_generated" sits LAST on purpose. These are virtually-staged / AI-rendered
// photos of a house that does not exist yet. They stay in the gallery so an
// operator can see what the listing is claiming, but every real photo is shown
// first, and they are excluded from all downstream analysis (repair cost,
// co-living, ARV) — see functions/underwritingV3/aiGeneratedImages.js.
const ROOM_ORDER = [
    "exterior", "roof", "living_area", "kitchen", "dining", "bedroom", "bathroom",
    "laundry", "garage", "basement", "crawl_basement", "attic", "mechanical",
    "hallway", "pool", "yard", "porch", "floor_plan", "other", "ai_generated"
];

const ROOM_LABEL = {
    exterior: "Exterior",
    roof: "Roof",
    living_area: "Living Area",
    kitchen: "Kitchen",
    dining: "Dining",
    bedroom: "Bedrooms",
    bathroom: "Bathrooms",
    laundry: "Laundry",
    garage: "Garage",
    basement: "Basement",
    // Legacy value. The classifier stopped emitting "crawl_basement" at
    // CLASSIFIER_VERSION 9 — every below-grade room is now "basement" and how
    // finished it is lives in the finish_level atom. Kept (and relabelled to
    // "Basement", NOT "Crawl Space") so already-published deals carrying v8
    // labels read correctly until they're re-classified/republished. A standable
    // unfinished basement is a basement, not a crawl space.
    crawl_basement: "Basement",
    attic: "Attic",
    mechanical: "Mechanical",
    hallway: "Hallway",
    pool: "Pool",
    yard: "Yard",
    porch: "Porch",
    floor_plan: "Floor Plan",
    other: "Other",
    ai_generated: "AI Slop"
};

const BUILDING_ORDER = ["main_house", "adu_guest_suite", "unclear"];
const BUILDING_PREFIX = {
    main_house: "Main House",
    adu_guest_suite: "ADU",
    unclear: "Unclear"
};

function normalizeUrl(u) {
    return typeof u === "string" ? u.trim() : "";
}

// Maps the interior aggregator's signal slugs (functions/flows/arvAgent/utils/
// interiorAggregatorV1.js — pushed into condition.luxurySignals /
// condition.lipstickSignals) to display labels + room scope. New signals added
// to the aggregator without an entry here fall through the humanizer below.
const SIGNAL_META = {
    // Kitchen — luxury
    quartzite_marble_slab:        { label: "Quartzite/marble slab counters", room: "kitchen" },
    waterfall_or_mitered_edge:    { label: "Waterfall/mitered counter edge", room: "kitchen" },
    panel_ready_appliances:       { label: "Panel-ready appliances", room: "kitchen" },
    slab_backsplash:              { label: "Slab stone backsplash", room: "kitchen" },
    feature_wall_backsplash:      { label: "Feature-wall backsplash", room: "kitchen" },
    slab_cabinets_new_or_recent:  { label: "New slab cabinets", room: "kitchen" },
    cabinets_flush_to_ceiling:    { label: "Cabinets flush to ceiling", room: "kitchen" },
    inset_cabinetry:              { label: "Inset cabinetry", room: "kitchen" },
    // Kitchen — lipstick
    kitchen_cabinets_painted_or_refaced: { label: "Cabinets painted/refaced (not replaced)", room: "kitchen" },
    kitchen_cabinets_original_dated:     { label: "Original/dated cabinets", room: "kitchen" },
    laminate_counter:             { label: "Laminate counters", room: "kitchen" },
    tile_counter:                 { label: "Tile counters", room: "kitchen" },
    dated_backsplash:             { label: "Dated tile backsplash", room: "kitchen" },
    four_inch_strip_backsplash:   { label: "4-inch strip backsplash", room: "kitchen" },
    // Bath — luxury
    largeformat_tile_surround:    { label: "Large-format tile surround", room: "bathroom" },
    // Bath — lipstick
    bath_vanity_painted_or_refaced: { label: "Vanity painted/refaced (not replaced)", room: "bathroom" },
    bath_vanity_original_dated:     { label: "Original/dated vanity", room: "bathroom" },
    fiberglass_tub_insert:        { label: "Fiberglass tub insert", room: "bathroom" },
    cultured_marble_surround:     { label: "Cultured marble surround", room: "bathroom" },
    dated_small_tile_surround:    { label: "Dated small-tile surround", room: "bathroom" },
    polished_brass_faucet:        { label: "Polished brass faucet", room: "bathroom" },
    two_handle_dated_faucet:      { label: "Two-handle dated faucet", room: "bathroom" },
    hollywood_vanity_bar:         { label: "Hollywood vanity bar lighting", room: "bathroom" },
    // Whole-house — luxury
    wide_plank_hardwood:          { label: "Wide-plank hardwood flooring", room: "whole" },
    description_named_premium_features: { label: "Listing names premium features", room: "whole" },
    description_craftsmanship:    { label: "Listing cites craftsmanship", room: "whole" },
    // Whole-house — lipstick
    dated_small_tile_floor:       { label: "Dated small-tile flooring", room: "whole" },
    sheet_vinyl_floor:            { label: "Sheet vinyl flooring", room: "whole" },
    orange_oak_trim:              { label: "Orange oak trim", room: "whole" },
    wood_paneling_main_living:    { label: "Wood paneling in main living area", room: "whole" },
    description_investor_budget:  { label: "Listing uses investor/budget language", room: "whole" },
    // Distress (functions/flows/arvAgent/utils/conditionAggregatorV1.js — evaluateDistress).
    // No per-room scoping at the atom layer, so all route to "whole".
    distress_visibleWaterDamage:       { label: "Visible water damage", room: "whole" },
    distress_missingOrDamagedDrywall:  { label: "Missing or damaged drywall", room: "whole" },
    distress_moldOrBiologicalStaining: { label: "Mold or biological staining", room: "whole" },
    distress_floorDamageOrRot:         { label: "Floor damage or rot", room: "whole" },
    distress_majorStructuralOrSystems: { label: "Major structural/systems damage", room: "whole" },
};

// Fallback humanizer for signals not in SIGNAL_META — converts the snake_case
// slug to "Sentence case" and infers room scope from the prefix.
function humanizeSignal(slug) {
    const meta = SIGNAL_META[slug];
    if (meta) return meta;
    const words = String(slug).replace(/_/g, " ").trim();
    const label = words.charAt(0).toUpperCase() + words.slice(1);
    let room = "whole";
    if (/^kitchen_|^kit_/.test(slug)) room = "kitchen";
    else if (/^bath_|^bathroom_/.test(slug)) room = "bathroom";
    return { label, room };
}

const TIER_LINE = {
    lipstick: "Lipstick-tier finish",
    middle:   "Mid-tier finish",
    luxury:   "Luxury-tier finish",
    high:     "Luxury-tier finish",
};

function collectRoomSignals(condition, room, limit) {
    const distress = Array.isArray(condition.distressSignals) ? condition.distressSignals : [];
    const luxury = Array.isArray(condition.luxurySignals) ? condition.luxurySignals : [];
    const lipstick = Array.isArray(condition.lipstickSignals) ? condition.lipstickSignals : [];
    const out = [];
    const seen = new Set();
    // Distress first — water damage / structural damage leads the section because
    // it's the most actionable signal for an investor. Luxury follows as the
    // "because of XYZ" positive evidence; lipstick as dragging-down evidence.
    for (const slug of [...distress, ...luxury, ...lipstick]) {
        const meta = humanizeSignal(slug);
        if (meta.room !== room) continue;
        if (seen.has(meta.label)) continue;
        seen.add(meta.label);
        out.push(meta.label);
        if (out.length >= limit) break;
    }
    return out;
}

function buildRoomBullets(roomType, condition) {
    if (!condition) return [];
    const obs = condition.observations || {};
    const isRemodeled = condition.condition === "Recently Remodeled";
    const tierLabel = isRemodeled && condition.remodelTier ? TIER_LINE[condition.remodelTier] : null;

    const bullets = [];
    const seen = new Set();
    const push = (s) => {
        if (!s || typeof s !== "string") return;
        const k = s.trim().toLowerCase();
        if (!k || seen.has(k)) return;
        seen.add(k);
        bullets.push(s.trim());
    };

    if (roomType === "kitchen") {
        if (tierLabel) push(tierLabel);
        if (obs.kitchen_cabinets === "original") push("Cabinets: original");
        else if (obs.kitchen_cabinets === "replaced") push("Cabinets: replaced");
        if (obs.kitchen_surfaces === "updated") push("Countertops: updated");
        else if (obs.kitchen_surfaces === "original") push("Countertops: original");
        for (const s of collectRoomSignals(condition, "kitchen", 3)) push(s);
    } else if (roomType === "bathroom") {
        if (tierLabel) push(tierLabel);
        if (obs.bathroom_shower_tub === "original") push("Shower/tub: original");
        else if (obs.bathroom_shower_tub === "replaced") push("Shower/tub: replaced");
        if (obs.bathroom_vanity === "original") push("Vanity: original");
        else if (obs.bathroom_vanity === "updated") push("Vanity: updated");
        for (const s of collectRoomSignals(condition, "bathroom", 3)) push(s);
    } else if (roomType === "living_area" || roomType === "hallway" || roomType === "dining") {
        if (obs.popcorn_ceilings === true) push("Popcorn ceilings present");
        for (const s of collectRoomSignals(condition, "whole", 3)) push(s);
    }

    return bullets.slice(0, 5);
}

// Single grid cell: holds its own load state so a grey shimmer placeholder
// stands in until the image paints, then the image fades in over it. The ref
// callback catches images already in the browser cache (their `load` event can
// fire before React attaches the onLoad handler when a virtualized row remounts).
function ImageCell({ url, imageIndex, left, columnWidth, rowHeight, cellPadding, source, sourceFavicons, subRoom, onClick, onError }) {
    const [loaded, setLoaded] = useState(false);

    const handleRef = useCallback((node) => {
        if (node && node.complete && node.naturalWidth > 0) setLoaded(true);
    }, []);

    return (
        <div
            style={{ position: 'absolute', left, top: 0, width: columnWidth, height: rowHeight, padding: cellPadding }}
            className="scrollable-image-cell"
        >
            {!loaded && <div className="scrollable-image-skeleton" />}
            <img
                ref={handleRef}
                src={url}
                alt={`Property ${imageIndex + 1}`}
                className={`scrollable-image scrollable-image-fade cursor-pointer${loaded ? ' is-loaded' : ''}`}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                onClick={onClick}
                onLoad={() => setLoaded(true)}
                onError={onError}
            />
            {/* Room tag — the floor plan's printed name for this room ("Family Room"),
                else the basement BEDROOM marker. Display-ready; see resolveSubRoomBadge.
                It's what makes a re-bucketed photo legible: the plan, not the photo's
                appearance, is why this shot sits under Dining. */}
            {subRoom && (
                <span className="scrollable-image-subroom xs-label-bold">
                    {subRoom}
                </span>
            )}
            <SourceBadge source={source} sourceFavicons={sourceFavicons} iconOnly />
        </div>
    );
}

function ScrollableImages(props) {
    const images = useMemo(() => {
        if (!props.images) return [];
        if (!Array.isArray(props.images)) return [];
        return props.images.filter(url => url != null);
    }, [props.images]);

    const closeScrollableImages = useMemo(() => {
        return props.closeScrollableImages || (() => {});
    }, [props.closeScrollableImages]);

    const imageClick = useMemo(() => {
        return props.imageClick || (() => {});
    }, [props.imageClick]);

    const onFailedCountChange = props.onFailedCountChange;

    const condition = props.condition;
    const imageLabels = props.imageLabels;
    // Optional. The co-living floor plan (coLivingInterior.floorPlan): its transcribed
    // rooms + the room locator's photo→room pins. Present it and the gallery grows a plan
    // that follows the scroll; absent (every non-co-living surface) and nothing changes.
    const floorPlan = props.floorPlan || null;
    const sourceLookup = useMemo(() => buildSourceLookup(props.imageSources), [props.imageSources]);
    const sourceFavicons = props.sourceFavicons || null;
    const containerRef = useRef(null);
    const [failedImages, setFailedImages] = useState(new Set());

    useEffect(() => {
        if (onFailedCountChange) {
            onFailedCountChange(failedImages.size);
        }
    }, [failedImages.size, onFailedCountChange]);

    const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const isMobileView = viewportSize.width <= 720;

    const COLUMN_COUNT = 2;
    const CELL_PADDING = 8;
    const HEADER_HEIGHT = 44;
    const GRID_WIDTH = isMobileView ? viewportSize.width : 750;
    const COLUMN_WIDTH = useMemo(() => GRID_WIDTH / COLUMN_COUNT, [GRID_WIDTH]);
    const ROW_HEIGHT = useMemo(() => {
        const imageWidth = COLUMN_WIDTH - (CELL_PADDING * 2);
        return imageWidth * 0.75 + (CELL_PADDING * 2);
    }, [COLUMN_WIDTH]);

    // Build URL → label lookup maps (primary: mlsPhotoUrl, fallback: firebaseUrl).
    // Returns null when no labels are present → triggers flat-grid fallback.
    const labelLookup = useMemo(() => {
        if (!imageLabels || !Array.isArray(imageLabels.labels) || imageLabels.labels.length === 0) {
            return null;
        }
        const byMls = new Map();
        const byFirebase = new Map();
        for (const label of imageLabels.labels) {
            const mls = normalizeUrl(label?.mlsPhotoUrl);
            const fb = normalizeUrl(label?.firebaseUrl);
            if (mls && !byMls.has(mls)) byMls.set(mls, label);
            if (fb && !byFirebase.has(fb)) byFirebase.set(fb, label);
        }
        return { byMls, byFirebase, aduPass: !!imageLabels.aduPass };
    }, [imageLabels]);

    // Build the flattened row array when grouping is active. Each row is either
    // { kind: "header", label, id } or { kind: "imagePair", cells: [{url, imageIndex, label}, ...] }.
    const groupedRows = useMemo(() => {
        if (!labelLookup) return null;
        const { byMls, byFirebase, aduPass } = labelLookup;

        // Bucket each image by (building, roomType)
        const buckets = new Map();
        const bucketKey = (building, room) => `${building || "_none_"}::${room || "other"}`;
        images.forEach((url, imageIndex) => {
            const n = normalizeUrl(url);
            const label = byMls.get(n) || byFirebase.get(n) || null;
            const building = aduPass ? (label?.building || "unclear") : "_none_";
            // displayCategory (floor_plan / porch) → the floor plan's room type → the
            // classifier's. The middle one is why a vacant dining room stops sitting in
            // Living Area: the plan knows what the room IS, the photo only shows what it
            // looks like. Unknown values fall through; nothing usable → "other".
            const roomType = resolveRoomBucket(label, ROOM_ORDER);
            const key = bucketKey(building, roomType);
            if (!buckets.has(key)) buckets.set(key, { building, roomType, items: [] });
            buckets.get(key).items.push({ url, imageIndex, label });
        });

        // Section order: building order (or single "_none_" bucket when !aduPass), then room order
        const buildingKeys = aduPass ? BUILDING_ORDER : ["_none_"];
        const orderedBuckets = [];
        for (const bKey of buildingKeys) {
            for (const rKey of ROOM_ORDER) {
                const key = bucketKey(bKey, rKey);
                if (buckets.has(key)) orderedBuckets.push(buckets.get(key));
            }
        }

        // Flatten buckets into header + image-pair rows
        const rows = [];
        let seenHeader = false;
        for (const bucket of orderedBuckets) {
            const roomLabel = ROOM_LABEL[bucket.roomType] || "Other";
            const prefix = bucket.building && bucket.building !== "_none_"
                ? `${BUILDING_PREFIX[bucket.building] || bucket.building} · `
                : "";
            rows.push({
                kind: "header",
                label: `${prefix}${roomLabel}`,
                id: `hdr-${bucket.building}-${bucket.roomType}`,
                isFirst: !seenHeader,
                roomType: bucket.roomType,
                building: bucket.building
            });
            seenHeader = true;
            for (let i = 0; i < bucket.items.length; i += COLUMN_COUNT) {
                rows.push({
                    kind: "imagePair",
                    cells: bucket.items.slice(i, i + COLUMN_COUNT),
                    id: `row-${bucket.building}-${bucket.roomType}-${i}`
                });
            }
        }
        return rows;
    }, [labelLookup, images]);

    const BULLET_LINE_HEIGHT = 20;
    const BULLET_BLOCK_PADDING = 8;

    const roomInsights = useMemo(() => {
        if (!groupedRows) return null;
        const map = new Map();
        for (const row of groupedRows) {
            if (row.kind !== "header") continue;
            const bullets = buildRoomBullets(row.roomType, condition);
            if (bullets.length > 0) map.set(row.id, bullets);
        }
        return map;
    }, [groupedRows, condition]);

    const getHeaderSize = (row) => {
        if (!row || row.kind !== "header") return HEADER_HEIGHT;
        const bullets = roomInsights?.get(row.id);
        if (!bullets || bullets.length === 0) return HEADER_HEIGHT;
        return HEADER_HEIGHT + bullets.length * BULLET_LINE_HEIGHT + BULLET_BLOCK_PADDING;
    };

    const sectionChips = useMemo(() => {
        if (!groupedRows) return null;
        const chips = [];
        let current = null;
        let offset = 0;
        for (let i = 0; i < groupedRows.length; i++) {
            const row = groupedRows[i];
            const size = row.kind === "header" ? getHeaderSize(row) : ROW_HEIGHT;
            if (row.kind === "header") {
                current = { id: row.id, label: row.label, count: 0, rowIndex: i, offset };
                chips.push(current);
            } else if (row.kind === "imagePair" && current) {
                for (const cell of row.cells) {
                    if (!failedImages.has(cell.imageIndex)) current.count += 1;
                }
            }
            offset += size;
        }
        return chips.filter(c => c.count > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedRows, failedImages, ROW_HEIGHT, roomInsights]);

    const showChipBar = sectionChips !== null && sectionChips.length > 1;

    const ROW_COUNT = useMemo(() => {
        if (groupedRows) return Math.max(1, groupedRows.length);
        return Math.max(1, Math.ceil(images.length / COLUMN_COUNT));
    }, [groupedRows, images.length]);

    const rowVirtualizer = useVirtualizer({
        count: ROW_COUNT,
        getScrollElement: () => containerRef.current,
        estimateSize: (index) => {
            if (groupedRows && groupedRows[index]?.kind === "header") return getHeaderSize(groupedRows[index]);
            return ROW_HEIGHT;
        },
        overscan: 8,
        getItemKey: (index) => groupedRows ? (groupedRows[index]?.id || `row-${index}`) : `row-${index}`,
    });

    useEffect(() => {
        if (rowVirtualizer && typeof rowVirtualizer.measure === "function") {
            rowVirtualizer.measure();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomInsights, GRID_WIDTH]);

    // ── The plan that follows the scroll ────────────────────────────────────────
    // V1 persisted both halves of this join and nothing consumed them together:
    // floorPlan.photoRooms is url → roomId (roomLocatorV1, and only where its two reads
    // AGREED), and floorPlan.rooms is roomId → the room's printed label + its bbox on a
    // sheet. Joined, they answer "which room is this photo of, and where is that room".
    const planSheets = useMemo(
        () => (Array.isArray(floorPlan?.imageUrls) ? floorPlan.imageUrls : []),
        [floorPlan]
    );
    const planRooms = useMemo(
        () => (Array.isArray(floorPlan?.rooms) ? floorPlan.rooms : []),
        [floorPlan]
    );
    // url → the room it was shot in. A photo the locator couldn't place simply isn't in
    // here — it gets no room, and the panel says so rather than guessing one.
    const roomByUrl = useMemo(() => {
        const pins = (floorPlan?.photoRooms && typeof floorPlan.photoRooms === 'object')
            ? floorPlan.photoRooms : null;
        const out = new Map();
        if (!pins || !planRooms.length) return out;
        const byId = new Map(planRooms.map((r) => [r.roomId, r]));
        for (const [url, loc] of Object.entries(pins)) {
            const room = byId.get(loc?.roomId);
            const u = normalizeUrl(url);
            if (room && u) out.set(u, room);
        }
        return out;
    }, [floorPlan, planRooms]);

    // Where each image row sits in the scroller, and which room it shows. Mirrors the
    // offset walk sectionChips already does — same row sizes, so they can't disagree.
    const planRowIndex = useMemo(() => {
        if (!groupedRows || roomByUrl.size === 0) return null;
        const rows = [];
        const firstRowOfRoom = new Map();
        let offset = 0;
        for (let i = 0; i < groupedRows.length; i++) {
            const row = groupedRows[i];
            const size = row.kind === "header" ? getHeaderSize(row) : ROW_HEIGHT;
            if (row.kind === "imagePair") {
                // The row's room is its FIRST placed cell — the photo at the top-left of
                // what you're reading.
                const cell = row.cells.find((c) => roomByUrl.has(normalizeUrl(c.url)));
                const room = cell ? roomByUrl.get(normalizeUrl(cell.url)) : null;
                rows.push({ offset, room });
                if (room && !firstRowOfRoom.has(room.roomId)) firstRowOfRoom.set(room.roomId, i);
            }
            offset += size;
        }
        return { rows, firstRowOfRoom };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedRows, roomByUrl, ROW_HEIGHT, roomInsights]);

    const scrollOffset = rowVirtualizer.scrollOffset ?? 0;

    // The room whose photos are at the top of the viewport. Null when that photo has no
    // pin — the panel then explicitly says it isn't placed, rather than leaving the last
    // room lit and quietly lying about where you are.
    const activePlanRoom = useMemo(() => {
        if (!planRowIndex || !planRowIndex.rows.length) return null;
        let current = planRowIndex.rows[0];
        for (const r of planRowIndex.rows) {
            if (r.offset <= scrollOffset + 4) current = r;
            else break;
        }
        return current.room;
    }, [planRowIndex, scrollOffset]);

    // The plan rides in the overlay's existing right-hand column, so it costs no width the
    // layout wasn't already spending — it just needs the screen to be wide enough for that
    // column to sit beside a 750px grid rather than under it.
    const showPlanTracker = !!planRowIndex && planSheets.length > 0 && viewportSize.width >= 1200;

    const roomHasPhotos = useCallback(
        (roomId) => !!planRowIndex?.firstRowOfRoom.has(roomId),
        [planRowIndex]
    );
    const scrollToRoom = useCallback((roomId) => {
        const idx = planRowIndex?.firstRowOfRoom.get(roomId);
        if (idx != null) rowVirtualizer.scrollToIndex(idx, { align: 'start' });
    }, [planRowIndex, rowVirtualizer]);

    const activeSectionId = useMemo(() => {
        if (!sectionChips || sectionChips.length === 0) return null;
        let active = sectionChips[0].id;
        for (const chip of sectionChips) {
            if (chip.offset <= scrollOffset + 4) active = chip.id;
            else break;
        }
        return active;
    }, [sectionChips, scrollOffset]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeScrollableImages();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [closeScrollableImages]);

    useEffect(() => {
        const handleResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock the page behind the overlay. The overlay is fixed and full-screen, but the
    // only scrollable thing in it is the grid column — a wheel over the gutters, the
    // back button, or the plan/condition column lands on a non-scrolling element and the
    // browser hands the scroll to the document underneath.
    useEffect(() => {
        document.body.classList.add('scrollable-images-scroll-locked');
        return () => document.body.classList.remove('scrollable-images-scroll-locked');
    }, []);

    const chipBarRef = useRef(null);
    const [chipBarHeight, setChipBarHeight] = useState(0);

    useLayoutEffect(() => {
        if (!showChipBar) {
            setChipBarHeight(0);
            return;
        }
        const el = chipBarRef.current;
        if (!el) return;
        const update = () => setChipBarHeight(el.getBoundingClientRect().height);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [showChipBar, sectionChips]);

    const exitContainerRef = useRef(null);
    const [exitContainerHeight, setExitContainerHeight] = useState(0);

    useLayoutEffect(() => {
        if (!isMobileView) {
            setExitContainerHeight(0);
            return;
        }
        const el = exitContainerRef.current;
        if (!el) return;
        const update = () => setExitContainerHeight(el.getBoundingClientRect().height);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isMobileView]);

    // On desktop the overlay is offset below the global header by
    // --app-header-offset (0 on pages without one — see ScrollableImages.css /
    // Layout.css) plus its own 40px top padding; mobile is full-screen and
    // subtracts its in-overlay back-button row instead.
    const [headerOffset, setHeaderOffset] = useState(0);
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const raw = getComputedStyle(el).getPropertyValue('--app-header-offset');
        const parsed = parseInt(raw, 10);
        setHeaderOffset(Number.isFinite(parsed) ? parsed : 0);
    }, [viewportSize.width]);

    const gridHeight = viewportSize.height - (isMobileView ? exitContainerHeight : (headerOffset + 40)) - chipBarHeight;

    if (!images || images.length === 0) {
        return (
            <div className="scrollable-images-outer-container bg-colour-white">
                <div ref={exitContainerRef} className="scrollable-images-exit-container">
                    <div
                        className="text-button button-row gap-small bg-colour-white scrollable-image-back-button"
                        onClick={() => closeScrollableImages()}
                    >
                        <Arrow
                            className=""
                            fill={colour.gray06}
                        />
                        <span className="body-bold colour-primary">
                            Back
                        </span>
                    </div>
                </div>
                <div className="scrollable-images-container">
                    <div className="scrollable-images-empty">
                        <span className="body-regular colour-secondary">No images available</span>
                    </div>
                </div>
            </div>
        );
    }

    const renderFlatCell = (vRow) => {
        const top = vRow.start;
        return (
            <div
                key={vRow.key}
                style={{ position: 'absolute', top, left: 0, height: vRow.size, width: '100%' }}
            >
                {[0, 1].map((col) => {
                    const imageIndex = vRow.index * COLUMN_COUNT + col;
                    const imageUrl = images[imageIndex];
                    const left = col * COLUMN_WIDTH;
                    if (!imageUrl || failedImages.has(imageIndex)) return null;
                    return (
                        <ImageCell
                            key={`${vRow.index}-${col}-${imageIndex}`}
                            url={imageUrl}
                            imageIndex={imageIndex}
                            left={left}
                            columnWidth={COLUMN_WIDTH}
                            rowHeight={ROW_HEIGHT}
                            cellPadding={CELL_PADDING}
                            source={resolveImageSource(sourceLookup, imageUrl)}
                            sourceFavicons={sourceFavicons}
                            onClick={() => imageClick(imageIndex)}
                            onError={() => setFailedImages(prev => new Set(prev).add(imageIndex))}
                        />
                    );
                })}
            </div>
        );
    };

    const renderGroupedRow = (vRow) => {
        const top = vRow.start;
        const row = groupedRows[vRow.index];
        if (!row) return null;

        if (row.kind === "header") {
            const bullets = roomInsights?.get(row.id) || [];
            const headerClass = row.isFirst
                ? "scrollable-images-section-header scrollable-images-section-header--first"
                : "scrollable-images-section-header";
            return (
                <div
                    key={vRow.key}
                    style={{ position: 'absolute', top, left: 0, height: vRow.size, width: '100%' }}
                    className={headerClass}
                >
                    <span className="heading-small-bold colour-primary">
                        {row.label}
                    </span>
                    {bullets.length > 0 && (
                        <ul className="scrollable-images-room-bullets">
                            {bullets.map((b, i) => (
                                <li key={i} className="body-regular colour-secondary">
                                    {b}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            );
        }

        return (
            <div
                key={vRow.key}
                style={{ position: 'absolute', top, left: 0, height: vRow.size, width: '100%' }}
            >
                {row.cells.map((cell, col) => {
                    const { url, imageIndex, label } = cell;
                    const left = col * COLUMN_WIDTH;
                    if (!url || failedImages.has(imageIndex)) return null;
                    return (
                        <ImageCell
                            key={`${row.id}-${col}-${imageIndex}`}
                            url={url}
                            imageIndex={imageIndex}
                            left={left}
                            columnWidth={COLUMN_WIDTH}
                            rowHeight={ROW_HEIGHT}
                            cellPadding={CELL_PADDING}
                            source={resolveImageSource(sourceLookup, url, label)}
                            sourceFavicons={sourceFavicons}
                            subRoom={resolveSubRoomBadge(label)}
                            onClick={() => imageClick(imageIndex)}
                            onError={() => setFailedImages(prev => new Set(prev).add(imageIndex))}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className="scrollable-images-outer-container bg-colour-white">
            <div ref={exitContainerRef} className="scrollable-images-exit-container">
                <div
                    className="text-button button-row gap-small bg-colour-white scrollable-image-back-button"
                    onClick={() => closeScrollableImages()}
                >
                    <Arrow
                        className=""
                        fill={colour.gray06}
                    />
                    <span className="body-bold colour-primary">
                        Back
                    </span>
                </div>
            </div>
            <div className="scrollable-images-container">
                {
                    showChipBar &&
                    <div ref={chipBarRef} className="scrollable-images-chip-bar gap-small">
                        {sectionChips.map((section) => (
                            <FilterChip
                                key={section.id}
                                label={`${section.label} (${section.count})`}
                                selected={section.id === activeSectionId}
                                disabled={false}
                                leftIcon={null}
                                rightIcon={null}
                                index={section.rowIndex}
                                func={(idx) => rowVirtualizer.scrollToIndex(idx, { align: 'start' })}
                            />
                        ))}
                    </div>
                }
                <div
                    ref={containerRef}
                    style={{ overflowY: 'auto', height: gridHeight, width: GRID_WIDTH }}
                    className="scrollable-images-grid"
                >
                    <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                        {rowVirtualizer.getVirtualItems().map((vRow) => (
                            groupedRows ? renderGroupedRow(vRow) : renderFlatCell(vRow)
                        ))}
                    </div>
                </div>
            </div>
            {/* The right-hand column. This overlay has always been a three-column row —
                back button · grid · condition — and the plan belongs in that third column,
                not wedged in beside the grid: the grid's width is fixed (its cells are
                absolutely positioned off it), so anything sharing its container has to
                take the space out of the photos. */}
            {(condition !== null || showPlanTracker) && (
                <div className="scrollable-images-side" role="complementary" aria-label="Photo details">
                    {condition !== null && (
                        <div className="scrollable-images-condition-container">
                            <Condition
                                label={condition.condition}
                                iconBG={propertyConditionBG(condition.condition, false)}
                                darkMode={false}
                            />
                            {
                                condition.condition === "Recently Remodeled" &&
                                <FinishChip
                                    label={condition.remodelTier === "middle" ? "Mid-tier" : (condition.remodelTier === "luxury" || condition.remodelTier === "high") ? "Luxury" : "Lipstick"}
                                    level={condition.remodelTier === "middle" ? 1 : (condition.remodelTier === "luxury" || condition.remodelTier === "high") ? 2 : 0}
                                />
                            }
                        </div>
                    )}
                    {showPlanTracker && (
                        <CoLivingPlanTracker
                            sheets={planSheets}
                            rooms={planRooms}
                            activeRoom={activePlanRoom}
                            roomHasPhotos={roomHasPhotos}
                            onSelectRoom={scrollToRoom}
                        />
                    )}
                </div>
            )}
        </div>
    )
};

export default React.memo(ScrollableImages);
